import { X, Building2, CreditCard, Package, Calendar, User, Phone, Mail } from "lucide-react"
import { useLanguage } from "../context/LanguageProvider";

export default function ConfirmatorInvoiceModal({ confirmatorInvoice, isOpen, onClose, isPrintMode = false }) {
  const { t } = useLanguage();
  
  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A"
    const date = new Date(isoString)
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-100" },
      paid: { bg: "bg-green-600", text: "text-white", border: "border-green-600" },
      cancelled: { bg: "bg-red-100", text: "text-red-700", border: "border-red-100" },
      annulé: { bg: "bg-red-100", text: "text-red-700", border: "border-red-100" },
      pending_payment: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-100" },
    }

    const config = statusConfig[status?.toLowerCase()] || {
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
    }

    const displayStatus =
      {
        paid: t('cors_confirmator_invoice.paid'),
        cancelled: t('cors_confirmator_invoice.cancelled'),
        pending_payment: t('cors_confirmator_invoice.pending_payment'),
      }[status?.toLowerCase()] || status

    return (
      <span
        className={`inline-flex items-center px-3 py-[1px] rounded-full text-[10px] font-medium border ${config.bg} ${config.text} ${config.border}`}
      >
        {displayStatus}
      </span>
    )
  }

  if (!isOpen) return null

  if (isPrintMode) {
    return <div className="fixed inset-0 bg-white z-50">{/* <PrintConfirmatorInvoice confirmatorInvoice={confirmatorInvoice} /> */}</div>
  }

  if (!confirmatorInvoice) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center">{t('cors_confirmator_invoice.not_found')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 print:hidden bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('cors_confirmator_invoice.invoice')} {confirmatorInvoice.invoice_number}</h2>
              <p className="text-xs text-gray-600">{t('cors_confirmator_invoice.generated_on')} {formatDateTime(confirmatorInvoice.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-2 cursor-pointer hover:bg-red-400 hover:text-white rounded-full">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto custom-scrollbar max-h-[75vh] print:max-h-none print:overflow-visible">
          <div className="p-8 print:p-0 bg-white">
            {/* Header Section */}
            <div className="mb-8 print:mb-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-0.5">
                  <h1 className="text-xl font-bold text-gray-900">{t('cors_confirmator_invoice.invoice_title')}</h1>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="font-medium mb-2">{t('cors_confirmator_invoice.number')}:</span>
                    <span className="font-mono bg-gray-100 px-3 py-1 text-xs rounded-lg">{confirmatorInvoice.invoice_number}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="mb-4">{getStatusBadge(confirmatorInvoice.status)}</div>
                  <div className="flex text-xs gap-1">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{t('cors_confirmator_invoice.issue_date')}: </span>
                    </div>
                    <div className="text-gray-900">{formatDateTime(confirmatorInvoice.created_at)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Confirmator Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Confirmator Details */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl px-6 py-3 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">{t('cors_confirmator_invoice.confirmator_info')}</h3>
                </div>
                <div className="space-y-1 grid grid-cols-2">
                  <div className="flex items-start gap-3">
                    <User className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-gray-900">
                        {confirmatorInvoice.confirmator?.name || "N/A"}
                      </div>
                      <div className="text-[10px] text-gray-600 capitalize">
                        {confirmatorInvoice.confirmator?.gender?.toLowerCase() || "N/A"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-xs text-gray-700">{confirmatorInvoice.confirmator?.phone || "N/A"}</span>
                  </div>
                </div>
              </div>

              {/* Banking Info */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl px-6 py-3 border border-emerald-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">{t('cors_confirmator_invoice.banking_info')}</h3>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">{t('cors_confirmator_invoice.rib')}</div>
                      <div className="text-xs font-mono text-gray-900">{confirmatorInvoice.confirmator?.rib || "N/A"}</div>
                    </div>
                    {
                      confirmatorInvoice.confirmator?.price_work != 0 ? (
                        <div>
                          <div className="text-xs font-medium text-gray-600 mb-1">{t('cors_confirmator_invoice.work_price')}</div>
                          <div className="text-xs font-semibold text-gray-900">
                            {Number.parseFloat(confirmatorInvoice.confirmator?.price_work || 0).toFixed(2)}
                          </div>
                        </div>
                      )
                      : ''
                    }
                    {
                      confirmatorInvoice.confirmator?.price_confirm != 0 ?
                        <div>
                          <div className="text-xs font-medium text-gray-600 mb-1">{t('cors_confirmator_invoice.confirm_price')}</div>
                          <div className="text-xs font-semibold text-gray-900">
                            {Number.parseFloat(confirmatorInvoice.confirmator?.price_confirm || 0).toFixed(2)}
                          </div>
                        </div>
                      : ''
                    }
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    {confirmatorInvoice.payment_date && (
                      <div className="flex items-center gap-1">
                        <div className="text-xs font-medium text-gray-600">{t('cors_confirmator_invoice.payment_date')}:</div>
                        <div className="text-[11px] text-gray-900">{confirmatorInvoice.payment_date}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Orders Summary */}
            {confirmatorInvoice.order_confirmators && confirmatorInvoice.order_confirmators.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Package className="w-4 h-4 text-purple-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">{t('cors_confirmator_invoice.confirmed_orders')} ({confirmatorInvoice.order_confirmators_count})</h3>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="pl-6 pr-3 py-3 text-left text-xs font-semibold text-gray-900">{t('cors_confirmator_invoice.order')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_confirmator_invoice.seller')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_confirmator_invoice.products')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_confirmator_invoice.delivery')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_confirmator_invoice.total')}</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-900">{t('cors_confirmator_invoice.status')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {confirmatorInvoice.order_confirmators.map((orderConfirmator, index) => (
                          <tr key={orderConfirmator.id} className="hover:bg-gray-50">
                            <td className="pl-6 pr-3 py-2">
                              <div className="space-y-1">
                                <span className="font-mono text-xs bg-purple-100 px-2 py-1 rounded text-purple-700">
                                  #{orderConfirmator.order?.code}
                                </span>
                                <div className="text-[10px] text-gray-600">
                                  {formatDateTime(orderConfirmator.order?.created_at)}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-gray-700">
                              {orderConfirmator.order?.seller.name || "N/A"}
                            </td>
                            <td className="px-1 py-2 text-center text-xs font-semibold text-gray-800">
                              {orderConfirmator.order?.order_products?.length
                                ? (() => {
                                    const names = orderConfirmator.order.order_products.map(op => op.product?.name);
                                    const firstTwo = names.slice(0, 2).join(", ");
                                    const remaining = names.length - 2;
                                    return remaining > 0 ? `${firstTwo} +${remaining} more` : firstTwo;
                                  })()
                                : "-"}
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-gray-700">
                              {Number.parseFloat(orderConfirmator.order?.delivery_price || 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-center text-xs font-bold text-gray-900">
                              {Number.parseFloat(orderConfirmator.order?.total || 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {getStatusBadge(confirmatorInvoice?.status)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Earnings Summary */}
            <div className="mb-8">
              <div className="gap-4">
                <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                  <div className="">
                    <div className="text-sm font-medium text-green-700">{t('cors_confirmator_invoice.invoice_total')}</div>
                    <div className="text-xs text-green-600 mt-1">{t('cors_confirmator_invoice.total_amount_desc')}</div>
                    <div className="flex items-center font-medium gap-1 text-xs mt-1 text-green-600 ">
                    <div className="text-xs font-normal">{t('cors_confirmator_invoice.total_orders')}: </div>
                      {confirmatorInvoice.order_confirmators_count}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-green-600 mt-1">
                    {Number.parseFloat(confirmatorInvoice.total || 0).toFixed(2)} MAD
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500 mt-8 print:hidden">
              {t('cors_confirmator_invoice.footer_text')}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}